/**
 * CoevaluationIconProgress Component
 *
 * Displays icons and progress for activity and peer evaluation (coevaluation) in a lesson or course item.
 * Renders two links: one for activity evaluation and one for peer evaluation, each with its own progress icon.
 * The links and icons adapt based on user access rights and evaluation state.
 *
 * Features:
 * - Dynamic link generation based on LTI and URL context
 * - Conditional rendering of progress icons and links based on evaluation state
 * - Multi-language support via i18n
 * - PropTypes and defaultProps for robust type checking
 *
 * @param {Object} props - Component props
 * @param {Object} props.data - Data object containing course, video IDs, and evaluation info
 * @param {boolean} props.accessCourse - Whether the user has access to the course
 * @param {Array} props.userTrack - Array of user tracking objects for evaluation state
 * @returns {JSX.Element} The rendered coevaluation icons and links
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { getLinkLtik } from 'constants/LTIData';

import CourseProgress from 'components/UI/CourseProgress/CourseProgress';

import classes from './CoevaluationIconProgress.module.css';

const url = window.location.pathname.split('/');

const CoevaluationIconProgress = props => {
  const { t } = useTranslation(['course']);
  const [activityState, setActivityState] = useState(
    'without_starting'
  );
  const [coevalState, setCoevalState] = useState('without_starting');

  useEffect(() => {
    // Set activity evaluation state based on userTrack
    props.userTrack.forEach(track => {
      if (track.id_media === props.data.idVideo) {
        if (track.media_eval === 2) setActivityState('finished');
        else if (track.media_eval === 1) setActivityState('started');
        else if (track.media_eval === 0)
          setActivityState('without_starting');
      }
    });
    // Set coevaluation state based on userTrack
    props.userTrack.forEach(track => {
      if (track.id_media === props.data.idVideo) {
        if (track.media_coeval >= 3) setCoevalState('finished');
        else if (track.media_coeval >= 1) setCoevalState('started');
        else if (track.media_coeval === 0)
          setCoevalState('without_starting');
      }
    });
  }, []);

  return (
    <>
      {/* Only render if coevaluation is enabled for this lesson */}
      {props.data.evaluation.coevaluation === 1 && (
        <>
          {/* Activity evaluation link and icon */}
          <Link
            to={
              getLinkLtik()
                ? `/ac/${props.data.idCourse}/${
                    props.data.idVideo
                  }/?ltik=${getLinkLtik()}`
                : url[2] === 'a'
                ? `/a/ac/${props.data.idCourse}/${props.data.idVideo}`
                : url[2] === 'course'
                ? `/course/ac/${props.data.idCourse}/${props.data.idVideo}`
                : `/ac/${props.data.idCourse}/${props.data.idVideo}`
            }
            className={`${classes.productLesson} ${
              !props.accessCourse
                ? classes.productLessonDisabled
                : ''
            }`}
          >
            <CourseProgress
              activityState={activityState} // Prop passed: evaluation state for activity
              icon="activity" // Prop passed: icon type for activity
            />
            <p className={classes.productContentItemText}>
              {t('activity')}
              {/* Internationalized label for activity */}
            </p>
          </Link>
          {/* Peer evaluation (coevaluation) link and icon */}
          <Link
            to={
              getLinkLtik()
                ? `/coe/${props.data.idCourse}/${
                    props.data.idVideo
                  }/?ltik=${getLinkLtik()}`
                : url[2] === 'a'
                ? `/a/coe/${props.data.idCourse}/${props.data.idVideo}`
                : url[2] === 'course'
                ? `/course/coe/${props.data.idCourse}/${props.data.idVideo}`
                : `/coe/${props.data.idCourse}/${props.data.idVideo}`
            }
            className={`${classes.productLesson} ${
              !props.accessCourse
                ? classes.productLessonDisabled
                : ''
            }`}
          >
            <CourseProgress
              activityState={coevalState} // Prop passed: evaluation state for coevaluation
              icon="coeval" // Prop passed: icon type for coevaluation
            />
            <p className={classes.productContentItemText}>
              {t('peer_evaluation')}
              {/* Internationalized label for peer evaluation */}
            </p>
          </Link>
        </>
      )}
    </>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
CoevaluationIconProgress.propTypes = {
  /**
   * Data object containing course, video IDs, and evaluation info
   * Used to generate links and determine evaluation state
   * Passed as prop to Link and CourseProgress
   */
  data: PropTypes.shape({
    idCourse: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    idVideo: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    evaluation: PropTypes.shape({
      coevaluation: PropTypes.number
    })
  }),
  /**
   * Boolean indicating whether the user has access to the course
   * Controls link and icon rendering/styling
   * Passed as prop to Link and CourseProgress
   */
  accessCourse: PropTypes.bool,
  /**
   * Array of user tracking objects for evaluation state
   * Used to determine activity and coevaluation progress
   * Passed as prop to internal logic
   */
  userTrack: PropTypes.arrayOf(PropTypes.object)
};

CoevaluationIconProgress.defaultProps = {
  data: {
    idCourse: '',
    idVideo: '',
    evaluation: { coevaluation: 0 }
  },
  accessCourse: false,
  userTrack: []
};

export default CoevaluationIconProgress;
